import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["metadata", "connection", "outputFields"];
import { List } from 'immutable';
import { createImmutableObjectClass } from 'immutable-less/ImmutableObject';
import { BaseImmutableObject } from 'immutable-less/BaseImmutableObject';
import * as ActionTypes from 'automation-ui-action-features/constants/ActionTypes';
import * as ConnectorEdgeTypes from 'automation-ui-action-features/constants/ConnectorEdgeTypes';
import { clearMetadata } from 'automation-ui-action-features/actions/ActionMetadataConfigUtils';
import * as BranchConnectorKeys from 'automation-ui-action-features/constants/BranchConnectorKeys';
import BranchConnector from 'automation-ui-action-features/records/connection/BranchConnector';
import OutputField from 'automation-ui-action-features/action-outputs/records/OutputField';
import { buildMetadataForAction } from 'automation-ui-action-features/records/factories';
import { buildConnectionForAction } from 'automation-ui-action-features/records/factoryUtils';
import { createSingleConnection } from 'automation-ui-action-features/records/recordsUtils';
export default class Action extends createImmutableObjectClass({
  actionId: null,
  portalId: null,
  flowId: null,
  connection: null,
  actionType: null,
  actionTypeId: null,
  metadata: null,
  outputFields: null,
  customProperties: {}
}, {
  fromJSON: action => {
    const {
      outputFields
    } = action;
    return Object.assign({}, action, {
      metadata: buildMetadataForAction(action, {}),
      connection: buildConnectionForAction(action),
      outputFields: outputFields && List(outputFields.map(outputField => new OutputField(outputField)))
    });
  },
  toJSON: _ref => {
    let {
        metadata,
        connection,
        outputFields
      } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);
    return Object.assign({
      metadata: metadata && (metadata instanceof BaseImmutableObject ? metadata.toJS() :
      // TypeScript doesn't recognize toJS() if ImmutableObjects are mixed with Records
      metadata.toJS()),
      connection: connection && connection.toJSON(),
      outputFields: outputFields && outputFields.toJS()
    }, rest);
  }
}) {
  static withExtraOpts(action, extraOpts) {
    return new Action(action).set('metadata', buildMetadataForAction(action, extraOpts));
  }
  isNew() {
    return this.actionId === null;
  }
  getMetadata() {
    return this.metadata;
  }
  setMetadata(metadata) {
    return this.set('metadata', metadata);
  }
  getConnection() {
    return this.connection;
  }
  getActionType() {
    return this.actionType;
  }
  getActionTypeId() {
    return this.actionTypeId;
  }
  setActionTypeId(actionTypeId) {
    return this.set('actionTypeId', actionTypeId);
  }
  getActionId() {
    return this.actionId;
  }
  setActionId(actionId) {
    return this.set('actionId', actionId);
  }
  getOutputFields() {
    return this.outputFields;
  }
  getCustomProperty(propertyName) {
    return this.customProperties && this.customProperties[propertyName];
  }
  getFlowId() {
    return this.flowId;
  }
  updateMetadata(updater) {
    return this.update('metadata', metadata => updater(metadata));
  }
  isListBranch() {
    return this.actionType === ActionTypes.LIST_BRANCH;
  }
  isStaticBranch() {
    return this.actionType === ActionTypes.STATIC_BRANCH;
  }
  isABTestBranch() {
    return this.get('actionType') === ActionTypes.AB_TEST_BRANCH;
  }
  isLoopBranch() {
    return this.actionType === ActionTypes.WHILE_LOOP;
  }
  isBranch() {
    return this.isListBranch() || this.isStaticBranch() || this.isLoopBranch() || this.isABTestBranch();
  }

  /**
   * Given an `edgeId`, sets this Action's Connection to `null`.
   * For a single connection, it sets the entire
   * Connection to `null`. For a list branch it nullifies
   * the part of the Connection that relates to the `edgeId`
   * @param {String} edgeId
   */
  removeEdgeFromConnection(edgeId) {
    const connection = this.getConnection();
    if (!connection) {
      return this;
    }
    return this.updateEdgeInConnection(edgeId, null, false);
  }
  getNextActionIdByEdgeId(edgeId) {
    const connection = this.getConnection();
    const connector = connection && connection.getConnectorByEdgeId(edgeId);
    return connector && connector.getNextActionId();
  }
  isLeafNode() {
    const connection = this.getConnection();
    return !connection || connection.getConnectors().every(connector => !connector);
  }

  /**
   * Exits early if not a leaf node, this function always
   * returns `this` or a new instance of it when the edge
   * is added to the Action's Connection Record.
   */
  addEdgeToConnection(edgeId, nextActionId, isGoToEdge) {
    // leaf node condition
    if (this.getNextActionIdByEdgeId(edgeId)) {
      return this;
    }
    return this.updateEdgeInConnection(edgeId, nextActionId, isGoToEdge);
  }

  /**
   * Creates, deletes, or updates existing edges in the
   * Connection Record.
   *
   * @param {*} actionListBranchIndex for branching Actions, it
   *            determines which list branch edge (or default edge)
   *            to operate on
   * @param {*} nextActionId          if defined, updates the given
   *            Connection. Otherwise, it deletes the edge.
   * @param {*} isGoToEdge            determines edgeType
   * @return    this Action with an updated Connection
   */
  updateEdgeInConnection(edgeId, nextActionId, isGoToEdge) {
    const edgeType = isGoToEdge ? ConnectorEdgeTypes.GOTO : ConnectorEdgeTypes.STANDARD;
    const connector = nextActionId ? new BranchConnector({
      [BranchConnectorKeys.NEXT_ACTION_ID]: nextActionId,
      [BranchConnectorKeys.EDGE_TYPE]: edgeType
    }) : null;
    let actionConnection = this.getConnection();
    if (!actionConnection) {
      actionConnection = createSingleConnection(nextActionId, edgeType);
    } else {
      actionConnection = actionConnection.setConnectorByEdgeId(edgeId, connector);
    }
    return this.setConnection(actionConnection);
  }
  setOutputFields(outputFields) {
    const outputFieldsList = outputFields ? List(outputFields) : List();
    return this.set('outputFields', outputFieldsList);
  }
  setCustomProperty(key, value) {
    const customProperties = this.customProperties || {};
    return this.set('customProperties', Object.assign({}, customProperties, {
      [key]: value
    }));
  }
  setConnection(connection) {
    return this.set('connection', connection);
  }

  /**
   * @param {*} branchIndex (optional) Used when the
   *                            action is a branch
   *                            to specify which edge to use.
   *
   * @return a Connector record (if one exists) at the requested
   * branchIndex (if applicable).
   */
  findNextConnector(branchIndex) {
    const connection = this.getConnection();
    let nextConnector;
    if (this.isBranch() && connection) {
      nextConnector = connection.getConnectorByBranchIndex(branchIndex);
    } else {
      nextConnector = connection;
    }
    return nextConnector;
  }
  clearMetadata(basedOnAction) {
    return clearMetadata(this, basedOnAction);
  }

  // This getter is a placeholder method for returning the actionSetId
  // if an action is part of an action set. BE data model is still a WIP.
  getParentActionSetId() {
    return this.getCustomProperty('parentActionSetId');
  }
}